

export const dynamic = "force-dynamic";

import { useState, useMemo, useEffect, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Check, RotateCcw, Pencil, Trash2, CalendarDays, Plus, AlertTriangle, RefreshCw, ClipboardList } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import ActionMenu, { type ActionMenuItem } from "../components/ActionMenu";
import SearchInput from "../components/SearchInput";
import TaskForm, { type TaskFormValues, type TaskFormInitial } from "./TaskForm";
import ImageViewer from "@/components/ImageViewer";
import { getImageUrl, byNewest } from "@/lib/utils";
import { tasksApi, apiClient, apiErrorMessage } from "@/lib/api";
import { formatDate, formatDateForInput, localToday } from "@/lib/data";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";

const columns = [
  { key: "pending", label: "Pending", color: "border-t-rose-400", dot: "bg-rose-400", bg: "bg-rose-50" },
  { key: "in_progress", label: "In Progress", color: "border-t-blue-400", dot: "bg-blue-400", bg: "bg-blue-50" },
  { key: "completed", label: "Completed", color: "border-t-emerald-400", dot: "bg-emerald-400", bg: "bg-emerald-50" }
] as const;

type TaskStatus = typeof columns[number]["key"];

const priorityConfig = {
  high: { color: "text-rose-600", bg: "bg-rose-50", icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" },
  medium: { color: "text-amber-600", bg: "bg-amber-50", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
  low: { color: "text-stone-500", bg: "bg-stone-50", icon: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }
};

interface Task {
  id: number;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  /** Y-m-d */
  due_date?: string | null;
  /** Assignee's name (the API maps the id to a name) */
  assigned_to?: string | null;
  assigned_to_id?: number | null;
  event_id?: number | null;
  event?: { id: number; name: string; start_date?: string | null } | null;
  before_image?: string | null;
  after_image?: string | null;
  created_at?: string;
}

const OPEN_STATUSES = ["pending", "in_progress"];
const isOpen = (t: Task) => OPEN_STATUSES.includes(t.status);
const d10 = (s?: string | null) => (s || "").slice(0, 10);
/** Only unfinished tasks can be overdue */
const isOverdue = (t: Task, today: string) => isOpen(t) && !!t.due_date && d10(t.due_date) < today;
/** Y-m-d read as a local date, so the day never shifts */
const dueText = (d?: string | null) => (d ? formatDate(`${d10(d)}T00:00:00`) : "-");
const statusLabel = (s: string) => (s === "in_progress" ? "In progress" : s.charAt(0).toUpperCase() + s.slice(1));

type FormState = { task: Task | null; eventId: string } | null;

function TasksPageContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"kanban" | "table">("kanban");
  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState<FormState>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Task | null>(null);
  const [busy, setBusy] = useState(false);
  const [imageViewer, setImageViewer] = useState<{ url: string; title: string } | null>(null);

  const today = localToday();

  const loadTasks = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      // Staff only see their own assigned tasks; privileged roles see all
      const response = isStaff ? await tasksApi.getMyTasks() : await tasksApi.list();
      setTasks(response.data?.data || response.data || []);
      setLoadError(null);
    } catch (error) {
      if (!quiet) setLoadError(apiErrorMessage(error, "Couldn't load tasks"));
      else showToast(apiErrorMessage(error, "Couldn't refresh tasks"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  // /tasks?new=1&event_id=123 opens the new-task form with that event chosen
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    const eventId = searchParams.get("event_id") || "";
    setForm({ task: null, eventId: /^\d+$/.test(eventId) ? eventId : "" });
    const rest = new URLSearchParams(searchParams);
    rest.delete("new");
    rest.delete("event_id");
    setSearchParams(rest, { replace: true });
  }, [searchParams, setSearchParams]);

  // Newest-created tasks first (board columns and list view)
  const filteredTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matched = !q ? tasks : tasks.filter(t =>
      t.title?.toLowerCase().includes(q) ||
      t.assigned_to?.toLowerCase().includes(q) ||
      t.event?.name?.toLowerCase().includes(q)
    );
    return [...matched].sort(byNewest);
  }, [tasks, searchQuery]);

  const overdueCount = useMemo(() => tasks.filter(t => isOverdue(t, today)).length, [tasks, today]);

  const openNew = () => setForm({ task: null, eventId: "" });
  const openEdit = (t: Task) => setForm({ task: t, eventId: t.event_id ? String(t.event_id) : "" });
  const closeForm = () => { if (!saving) setForm(null); };

  const setStatusLocally = (id: number, status: string) =>
    setTasks(prev => prev.map(t => (t.id === id ? { ...t, status } : t)));

  const handleFormSubmit = async (values: TaskFormValues) => {
    if (!form) return;
    const editing = form.task;
    const fields = {
      title: values.title.trim(),
      status: values.status || "pending",
      priority: values.priority || "medium",
      assigned_to_id: values.assignedToId,
      event_id: values.eventId, // "" takes the task off its event
      due_date: values.dueDate,
      description: values.notes.trim(),
    };
    const hasPhoto = values.before_image instanceof File || values.after_image instanceof File;

    setSaving(true);
    try {
      if (hasPhoto) {
        // Multipart: let the browser set Content-Type (it adds the boundary Laravel needs)
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
        if (values.before_image instanceof File) formData.append("before_image", values.before_image);
        if (values.after_image instanceof File) formData.append("after_image", values.after_image);
        if (editing) {
          // PHP only reads multipart bodies on POST, so tunnel the PUT
          formData.append("_method", "PUT");
          await apiClient.post(`/tasks/${editing.id}`, formData);
        } else {
          await tasksApi.create(formData);
        }
      } else {
        const payload = {
          ...fields,
          assigned_to_id: Number(fields.assigned_to_id),
          event_id: fields.event_id ? Number(fields.event_id) : null,
          due_date: fields.due_date || null,
          description: fields.description || null,
        };
        if (editing) await tasksApi.update(editing.id, payload);
        else await tasksApi.create(payload);
      }
      showToast(editing ? "Task saved" : "Task added", "success");
      setForm(null);
      loadTasks(true);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't save the task"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (t: Task) => {
    const previous = t.status;
    setStatusLocally(t.id, "completed");
    try {
      await tasksApi.complete(t.id);
      showToast(`“${t.title}” marked done`, "success");
    } catch (error) {
      setStatusLocally(t.id, previous);
      showToast(apiErrorMessage(error, "Couldn't mark the task done"), "error");
    }
  };

  const handleReopen = async (t: Task) => {
    const previous = t.status;
    setStatusLocally(t.id, "pending");
    try {
      await tasksApi.update(t.id, { status: "pending" });
      showToast(`“${t.title}” reopened`, "success");
    } catch (error) {
      setStatusLocally(t.id, previous);
      showToast(apiErrorMessage(error, "Couldn't reopen the task"), "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await tasksApi.delete(deleting.id);
      setTasks(prev => prev.filter(t => t.id !== deleting.id));
      showToast(`“${deleting.title}” deleted`, "success");
      setDeleting(null);
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't delete the task"), "error");
    } finally {
      setBusy(false);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Update task status locally first for UI responsiveness
    const newStatus = destination.droppableId as TaskStatus;
    const taskId = parseInt(draggableId);
    if (source.droppableId === destination.droppableId) return; // order within a column isn't saved

    setStatusLocally(taskId, newStatus);

    try {
      await tasksApi.update(taskId, { status: newStatus });
      showToast(`Moved to ${statusLabel(newStatus).toLowerCase()}`, "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't change the task's status"), "error");
      loadTasks(true); // Reload to get correct state
    }
  };

  const menuItems = (t: Task): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ label: "Edit", icon: Pencil, onClick: () => openEdit(t) }];
    if (!isOpen(t)) items.push({ label: "Reopen", icon: RotateCcw, onClick: () => handleReopen(t) });
    if (t.event_id) items.push({ label: "Open event", icon: CalendarDays, onClick: () => navigate(`/events/detail?id=${t.event_id}`) });
    // Staff cannot delete tasks
    if (!isStaff) items.push({ label: "Delete…", icon: Trash2, danger: true, separated: true, onClick: () => setDeleting(t) });
    return items;
  };

  const doneButton = (t: Task, big = false) =>
    isOpen(t) ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleComplete(t); }}
        className={`${big ? "h-10 px-3.5 text-sm" : "h-10 md:h-8 px-2.5 text-xs"} inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold whitespace-nowrap`}
        title="Mark this task done"
      >
        <Check className="w-4 h-4" /> Mark done
      </button>
    ) : null;

  const duePill = (t: Task) => {
    if (!isOpen(t) || !t.due_date) return null;
    if (isOverdue(t, today)) return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700">Overdue</span>;
    if (d10(t.due_date) === today) return <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">Today</span>;
    return null;
  };

  const eventLine = (t: Task) =>
    t.event ? `${t.event.name}${t.event.start_date ? ` · ${formatDate(t.event.start_date)}` : ""}` : "No event";

  const photoThumbs = (t: Task, size: "sm" | "md") => {
    if (!t.before_image && !t.after_image) return null;
    const box = size === "sm" ? "h-10 w-10" : "w-16 h-16";
    const thumb = (path: string, title: string, tag: string, tagClass: string) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setImageViewer({ url: getImageUrl(path), title }); }}
        className={`relative ${box} shrink-0 rounded-lg overflow-hidden border border-stone-200 hover:opacity-90 transition-opacity`}
        aria-label={`View ${title.toLowerCase()}`}
      >
        <img src={getImageUrl(path)} alt={title} className="w-full h-full object-cover" />
        <span className={`absolute top-0 left-0 ${tagClass} text-white text-[8px] px-1 py-0.5 rounded-br`}>{tag}</span>
      </button>
    );
    return (
      <div className="flex gap-2">
        {t.before_image && thumb(t.before_image, "Before photo", size === "sm" ? "B" : "Before", "bg-stone-500")}
        {t.after_image && thumb(t.after_image, "After photo", size === "sm" ? "A" : "After", "bg-emerald-500")}
      </div>
    );
  };

  const emptyState = (
    <div className="text-center py-12 px-4">
      <ClipboardList className="w-12 h-12 mx-auto mb-3 text-stone-300" />
      {tasks.length === 0 ? (
        <>
          <p className="text-stone-600 font-medium">No tasks yet</p>
          <p className="text-sm text-stone-400 mt-1">Add a task to give someone a job and a due date.</p>
          <button type="button" onClick={openNew} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90">
            <Plus className="w-4 h-4" /> Add task
          </button>
        </>
      ) : (
        <>
          <p className="text-stone-600 font-medium">No tasks match “{searchQuery.trim()}”</p>
          <button type="button" onClick={() => setSearchQuery("")} className="mt-3 h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">Clear search</button>
        </>
      )}
    </div>
  );

  // Phone card for the list view
  const renderTaskCard = (t: Task) => {
    const priority = priorityConfig[t.priority as keyof typeof priorityConfig];
    const overdue = isOverdue(t, today);
    return (
      <div key={t.id} className={`p-4 ${isOpen(t) ? "" : "opacity-80"}`}>
        <div className="flex items-start justify-between gap-3">
          <button type="button" className="min-w-0 text-left" onClick={() => openEdit(t)}>
            <p className={`font-semibold break-words ${isOpen(t) ? "text-stone-800" : "text-stone-500 line-through"}`}>{t.title}</p>
            <p className="text-xs text-stone-500 mt-0.5 truncate">{eventLine(t)}</p>
          </button>
          <div className="shrink-0">
            <Badge label={statusLabel(t.status)} status={t.status} />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
          <div className="min-w-0">
            <p className="text-[11px] text-stone-400">Assigned to</p>
            <p className="text-stone-700 truncate">{t.assigned_to || "-"}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-stone-400">Due</p>
            <p className={`truncate ${overdue ? "text-rose-600 font-semibold" : "text-stone-700"}`}>{dueText(t.due_date)}</p>
            <div className="mt-0.5">{duePill(t)}</div>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-stone-400">Priority</p>
            <p className={`capitalize font-medium ${priority?.color || "text-stone-500"}`}>{t.priority || "-"}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          {photoThumbs(t, "sm") || <span />}
          <div className="flex items-center gap-2">
            {doneButton(t, true)}
            <ActionMenu items={menuItems(t)} label={`More actions for ${t.title}`} />
          </div>
        </div>
      </div>
    );
  };

  const listView = (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      {/* Phone cards */}
      <div className="md:hidden">
        <div className="px-4 py-2.5 border-b border-stone-100 bg-stone-50 text-xs text-stone-500">
          {filteredTasks.length} task{filteredTasks.length === 1 ? "" : "s"} · newest first
        </div>
        {filteredTasks.length === 0 ? emptyState : (
          <div className="divide-y divide-stone-100">{filteredTasks.map(renderTaskCard)}</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        {filteredTasks.length === 0 ? emptyState : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                {["Task", "Assigned to", "Due", "Priority", "Status"].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-stone-500 whitespace-nowrap">{h}</th>
                ))}
                <th className="px-3 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(t => {
                const priority = priorityConfig[t.priority as keyof typeof priorityConfig];
                const overdue = isOverdue(t, today);
                return (
                  <tr
                    key={t.id}
                    onClick={() => openEdit(t)}
                    className="border-b border-stone-100 cursor-pointer transition-colors hover:bg-amber-50/40"
                  >
                    <td className="px-3 py-3 min-w-[220px]">
                      <p className={`text-sm font-semibold ${isOpen(t) ? "text-stone-800" : "text-stone-500 line-through"}`}>{t.title}</p>
                      <p className="text-xs text-stone-500">{eventLine(t)}</p>
                      {(t.before_image || t.after_image) && <div className="mt-1.5">{photoThumbs(t, "sm")}</div>}
                    </td>
                    <td className="px-3 py-3 text-sm text-stone-700 whitespace-nowrap">{t.assigned_to || "-"}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <p className={`text-sm ${overdue ? "text-rose-600 font-semibold" : "text-stone-700"}`}>{dueText(t.due_date)}</p>
                      <div className="mt-0.5">{duePill(t)}</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className={`flex items-center gap-1.5 ${priority?.color || 'text-stone-500'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={priority?.icon || priorityConfig.medium.icon} />
                        </svg>
                        <span className="text-xs font-medium capitalize">{t.priority}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3"><Badge label={statusLabel(t.status)} status={t.status} /></td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {doneButton(t)}
                        <ActionMenu items={menuItems(t)} label={`More actions for ${t.title}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const kanbanView = (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map(col => {
          const colTasks = filteredTasks.filter(t => t.status === col.key);
          return (
            <div key={col.key} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${col.bg}`}>
                  <span className={`w-2 h-2 rounded-full ${col.dot}`}></span>
                  <span className="text-sm font-medium text-stone-700">{col.label}</span>
                  <span className="text-xs text-stone-500 bg-white px-1.5 py-0.5 rounded-full">{colTasks.length}</span>
                </div>
              </div>

              <Droppable droppableId={col.key}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`space-y-3 min-h-[200px] p-2 rounded-xl transition-colors ${
                      snapshot.isDraggingOver ? 'bg-stone-50/50 border-2 border-dashed border-amber-300' : ''
                    }`}
                  >
                    {colTasks.map((task, index) => {
                      const priority = priorityConfig[task.priority as keyof typeof priorityConfig];
                      const overdue = isOverdue(task, today);
                      return (
                        <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => openEdit(task)}
                              className={`bg-white rounded-xl p-4 border border-stone-100 cursor-pointer hover:shadow-md transition-all ${col.color} ${
                                snapshot.isDragging ? 'shadow-lg ring-2 ring-amber-200 rotate-2' : ''
                              }`}
                              style={{
                                ...provided.draggableProps.style,
                              }}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h4 className="font-semibold text-stone-800 text-sm">{task.title}</h4>
                                <svg className={`w-4 h-4 shrink-0 ${priority?.color || 'text-stone-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={priority?.icon || priorityConfig.medium.icon} />
                                </svg>
                              </div>
                              <p className="text-xs text-stone-400 mb-3">{task.event ? eventLine(task) : "-"}</p>

                              {/* Task Images */}
                              {(task.before_image || task.after_image) && (
                                <div className="mb-3 pt-2 border-t border-stone-100">
                                  <p className="text-[10px] font-medium text-stone-500 mb-2">Photos</p>
                                  {photoThumbs(task, "md")}
                                </div>
                              )}

                              <div className="flex items-center gap-3 text-xs">
                                <div className="flex items-center gap-1 text-stone-500 min-w-0">
                                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  <span className="truncate">{task.assigned_to || "-"}</span>
                                </div>
                                <div className={`flex items-center gap-1 ${overdue ? 'text-rose-600 font-semibold' : 'text-stone-400'}`}>
                                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  {dueText(task.due_date)}{overdue ? " · overdue" : ""}
                                </div>
                              </div>

                              <div className="mt-3 flex items-center justify-end gap-2">
                                {isOpen(task) ? doneButton(task) : (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleReopen(task); }}
                                    className="h-10 md:h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white text-xs font-semibold text-stone-600 hover:bg-stone-50"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" /> Reopen
                                  </button>
                                )}
                                <ActionMenu items={menuItems(task)} label={`More actions for ${task.title}`} />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {colTasks.length === 0 && (
                      <div className="text-center py-8 text-stone-400 text-sm">
                        Drop tasks here
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );

  let body: React.ReactNode;
  if (loading) {
    body = (
      <>
        <div className="h-10 w-72 max-w-full rounded-xl bg-stone-100 animate-pulse" />
        <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100" aria-label="Loading tasks">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="p-4 flex gap-4 items-center">
              <div className="flex-1 space-y-2"><div className="h-4 w-1/3 rounded bg-stone-100 animate-pulse" /><div className="h-3 w-1/4 rounded bg-stone-100 animate-pulse" /></div>
              <div className="h-8 w-24 rounded bg-stone-100 animate-pulse" />
            </div>
          ))}
        </div>
      </>
    );
  } else if (loadError) {
    body = (
      <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <p className="font-semibold text-stone-800">Couldn't load tasks</p>
        <p className="text-sm text-stone-500 mt-1">{loadError}</p>
        <button type="button" onClick={() => loadTasks()} className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {/* Search */}
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search tasks by title, assignee, or event..."
          className="max-w-md"
        />
        {tasks.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-xl">{emptyState}</div>
        ) : viewMode === "kanban" ? kanbanView : listView}
      </>
    );
  }

  const initialData: TaskFormInitial | undefined = form?.task ? {
    title: form.task.title,
    eventId: form.eventId,
    eventName: form.task.event?.name,
    eventDate: form.task.event?.start_date || undefined,
    assignedToId: form.task.assigned_to_id ? String(form.task.assigned_to_id) : "",
    assignedToName: form.task.assigned_to || undefined,
    dueDate: formatDateForInput(form.task.due_date),
    priority: form.task.priority,
    status: form.task.status,
    notes: form.task.description || "",
  } : form ? { eventId: form.eventId } : undefined;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-stone-500">
          {columns.map(c => {
            const count = tasks.filter(t => t.status === c.key).length;
            return (
              <span key={c.key} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${c.dot}`}></span>
                {c.label} <strong className="text-stone-700">{count}</strong>
              </span>
            );
          })}
          {overdueCount > 0 && (
            <span className="flex items-center gap-1.5 text-rose-600">
              <AlertTriangle className="w-3.5 h-3.5" /> Overdue <strong>{overdueCount}</strong>
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* View Toggle */}
          <div className="flex bg-stone-100 rounded-xl p-1" role="group" aria-label="View">
            <button
              type="button"
              aria-pressed={viewMode === "kanban"}
              onClick={() => setViewMode("kanban")}
              className={`h-9 px-3 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === "kanban" ? "bg-white text-stone-700 shadow-sm" : "text-stone-500"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Board
            </button>
            <button
              type="button"
              aria-pressed={viewMode === "table"}
              onClick={() => setViewMode("table")}
              className={`h-9 px-3 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === "table" ? "bg-white text-stone-700 shadow-sm" : "text-stone-500"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              List
            </button>
          </div>
          <button
            type="button"
            onClick={openNew}
            className="h-11 inline-flex items-center gap-1.5 text-sm font-semibold text-white px-4 rounded-xl gold-gradient shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add task
          </button>
        </div>
      </div>

      {body}

      {/* Add/Edit Task Modal */}
      <Modal
        isOpen={!!form}
        onClose={closeForm}
        title={form?.task ? "Edit task" : "New task"}
        size="md"
      >
        {form && (
          <TaskForm
            key={form.task ? `edit-${form.task.id}` : `new-${form.eventId}`}
            initialData={initialData}
            isEdit={!!form.task}
            saving={saving}
            onSubmit={handleFormSubmit}
            onCancel={closeForm}
          />
        )}
      </Modal>

      {/* Delete dialog */}
      {deleting && (
        <Modal
          isOpen={!!deleting}
          onClose={() => !busy && setDeleting(null)}
          title={`Delete “${deleting.title}”?`}
          size="sm"
          footer={
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setDeleting(null)} disabled={busy} className="h-11 px-4 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">Keep task</button>
              <button type="button" onClick={confirmDelete} disabled={busy} className="h-11 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">{busy ? "Deleting…" : "Delete task"}</button>
            </div>
          }
        >
          <div className="space-y-3 text-sm text-stone-600">
            <p>This removes the task{deleting.assigned_to ? ` assigned to ${deleting.assigned_to}` : ""} for good. It can't be undone.</p>
            {isOpen(deleting) && (
              <p className="rounded-lg bg-amber-50 text-amber-800 px-3 py-2">If the work is finished, mark it done instead so there's a record of it.</p>
            )}
          </div>
        </Modal>
      )}

      {/* Image Viewer Modal */}
      {imageViewer && (
        <ImageViewer
          imageUrl={imageViewer.url}
          title={imageViewer.title}
          isOpen={!!imageViewer}
          onClose={() => setImageViewer(null)}
        />
      )}
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    </DashboardLayout>
  );
}

export default function TasksPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <TasksPageContent />
    </Suspense>
  );
}
